import { US_STATES_CITIES } from './us-states-cities';
import { INDIA_STATES_CITIES } from './india-states-cities';
import { UK_STATES_CITIES } from './uk-states-cities';
import { CANADA_STATES_CITIES } from './canada-states-cities';
import { AUSTRALIA_STATES_CITIES } from './australia-states-cities';
import { UAE_STATES_CITIES } from './uae-states-cities';
import { PAKISTAN_STATES_CITIES } from './pakistan-states-cities';
import { BANGLADESH_STATES_CITIES } from './bangladesh-states-cities';
import { NEPAL_STATES_CITIES } from './nepal-states-cities';
import { SRILANKA_STATES_CITIES } from './srilanka-states-cities';
import { NIGERIA_STATES_CITIES } from './nigeria-states-cities';
import { GERMANY_STATES_CITIES } from './germany-states-cities';

// Countries with full state/city data
const DETAILED_COUNTRIES = {
  "US": { name: "United States", states: US_STATES_CITIES },
  "IN": { name: "India", states: INDIA_STATES_CITIES },
  "GB": { name: "United Kingdom", states: UK_STATES_CITIES },
  "CA": { name: "Canada", states: CANADA_STATES_CITIES },
  "AU": { name: "Australia", states: AUSTRALIA_STATES_CITIES },
  "AE": { name: "United Arab Emirates", states: UAE_STATES_CITIES },
  "PK": { name: "Pakistan", states: PAKISTAN_STATES_CITIES },
  "BD": { name: "Bangladesh", states: BANGLADESH_STATES_CITIES },
  "NP": { name: "Nepal", states: NEPAL_STATES_CITIES },
  "LK": { name: "Sri Lanka", states: SRILANKA_STATES_CITIES },
  "NG": { name: "Nigeria", states: NIGERIA_STATES_CITIES },
  "DE": { name: "Germany", states: GERMANY_STATES_CITIES }
};

// Helper to create a simple country entry with one "region" that allows free-text city
const simple = (name) => ({
  name,
  states: { "OT": { name: "All Regions", cities: [] } }
});

export const COUNTRIES = {
  ...DETAILED_COUNTRIES,
  // South Asia
  "AF": simple("Afghanistan"),
  // East Asia
  "JP": simple("Japan"),
  "KR": simple("South Korea"),
  "CN": simple("China"),
  "TW": simple("Taiwan"),
  "HK": simple("Hong Kong"),
  // Southeast Asia
  "SG": simple("Singapore"),
  "MY": simple("Malaysia"),
  "ID": simple("Indonesia"),
  "PH": simple("Philippines"),
  "TH": simple("Thailand"),
  "VN": simple("Vietnam"),
  "MM": simple("Myanmar"),
  // Middle East
  "SA": simple("Saudi Arabia"),
  "QA": simple("Qatar"),
  "KW": simple("Kuwait"),
  "OM": simple("Oman"),
  "BH": simple("Bahrain"),
  "IQ": simple("Iraq"),
  "IR": simple("Iran"),
  "SY": simple("Syria"),
  "LB": simple("Lebanon"),
  "JO": simple("Jordan"),
  "YE": simple("Yemen"),
  "PS": simple("Palestine"),
  "IL": simple("Israel"),
  // Africa - West
  "GH": simple("Ghana"),
  "SN": simple("Senegal"),
  "ML": simple("Mali"),
  "GM": simple("Gambia"),
  "GN": simple("Guinea"),
  "NE": simple("Niger"),
  "BF": simple("Burkina Faso"),
  "CI": simple("Ivory Coast"),
  "CM": simple("Cameroon"),
  "TD": simple("Chad"),
  "LR": simple("Liberia"),
  "SL": simple("Sierra Leone"),
  "MR": simple("Mauritania"),
  // Africa - East
  "KE": simple("Kenya"),
  "ET": simple("Ethiopia"),
  "TZ": simple("Tanzania"),
  "UG": simple("Uganda"),
  "RW": simple("Rwanda"),
  "SO": simple("Somalia"),
  "DJ": simple("Djibouti"),
  "ER": simple("Eritrea"),
  "SD": simple("Sudan"),
  // Africa - South
  "ZA": simple("South Africa"),
  "ZW": simple("Zimbabwe"),
  "MZ": simple("Mozambique"),
  // Africa - North
  "EG": simple("Egypt"),
  "MA": simple("Morocco"),
  "DZ": simple("Algeria"),
  "TN": simple("Tunisia"),
  "LY": simple("Libya"),
  // Europe - Western
  "FR": simple("France"),
  "NL": simple("Netherlands"),
  "BE": simple("Belgium"),
  "IE": simple("Ireland"),
  "AT": simple("Austria"),
  "CH": simple("Switzerland"),
  "IT": simple("Italy"),
  "ES": simple("Spain"),
  "PT": simple("Portugal"),
  // Europe - Northern
  "SE": simple("Sweden"),
  "NO": simple("Norway"),
  "DK": simple("Denmark"),
  "FI": simple("Finland"),
  "IS": simple("Iceland"),
  // Europe - Eastern
  "PL": simple("Poland"),
  "CZ": simple("Czech Republic"),
  "SK": simple("Slovakia"),
  "HU": simple("Hungary"),
  "RO": simple("Romania"),
  "BG": simple("Bulgaria"),
  "UA": simple("Ukraine"),
  "RS": simple("Serbia"),
  "HR": simple("Croatia"),
  "BA": simple("Bosnia and Herzegovina"),
  "MK": simple("North Macedonia"),
  "AL": simple("Albania"),
  "ME": simple("Montenegro"),
  "XK": simple("Kosovo"),
  "GR": simple("Greece"),
  "TR": simple("Turkey"),
  "RU": simple("Russia"),
  // Americas
  "MX": simple("Mexico"),
  "BR": simple("Brazil"),
  "AR": simple("Argentina"),
  "CO": simple("Colombia"),
  "PE": simple("Peru"),
  "CL": simple("Chile"),
  "EC": simple("Ecuador"),
  "BO": simple("Bolivia"),
  "PY": simple("Paraguay"),
  "UY": simple("Uruguay"),
  "VE": simple("Venezuela"),
  "GT": simple("Guatemala"),
  "CR": simple("Costa Rica"),
  "PA": simple("Panama"),
  "DO": simple("Dominican Republic"),
  "CU": simple("Cuba"),
  "JM": simple("Jamaica"),
  "TT": simple("Trinidad and Tobago"),
  // Oceania
  "NZ": simple("New Zealand"),
  "FJ": simple("Fiji"),
  // Central Asia
  "KZ": simple("Kazakhstan"),
  "UZ": simple("Uzbekistan"),
  "TJ": simple("Tajikistan"),
  "KG": simple("Kyrgyzstan"),
  "TM": simple("Turkmenistan"),
};

// Helper function to get all cities for a country/state
export const getCitiesForState = (countryCode, stateCode) => {
  if (!COUNTRIES[countryCode] || !COUNTRIES[countryCode].states[stateCode]) {
    return [];
  }
  return COUNTRIES[countryCode].states[stateCode].cities;
};

// Helper function to search cities (case-insensitive)
export const searchCities = (countryCode, stateCode, searchTerm) => {
  const cities = getCitiesForState(countryCode, stateCode);
  if (!searchTerm) return cities;
  
  const term = searchTerm.toLowerCase();
  return cities.filter(city => city.toLowerCase().includes(term));
};

// Helper function to normalize city name (for storage)
export const normalizeCity = (cityName) => {
  if (!cityName) return '';
  // Trim and convert to title case
  return cityName
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to find state code from state name
export const findStateCode = (countryCode, stateName) => {
  if (!stateName || !COUNTRIES[countryCode]) return '';
  
  const states = COUNTRIES[countryCode].states;
  const normalizedSearch = stateName.toLowerCase().trim();
  
  // Search for exact match or partial match
  for (const [code, stateData] of Object.entries(states)) {
    const normalizedStateName = stateData.name.toLowerCase();
    if (normalizedStateName === normalizedSearch || 
        normalizedStateName.includes(normalizedSearch) ||
        normalizedSearch.includes(normalizedStateName)) {
      return code;
    }
  }
  
  return ''; // Return empty if not found
};
